import { Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { EnergyInterfaceResponse } from '../../../../core/clients/omni-core/models';
import {
  EnergyInterface,
  energyInterfaceToModel,
} from '../energy-interface-details-edit-form/definitions';
import {
  EnergyInterfacesStore,
  toEnergyInterfacePayload,
} from '../../stores/energy-interfaces.store';
import { NotificationService, Button, InputSimple } from 'excellence-ui';
import { FormField, form, required } from '@angular/forms/signals';
import { NgIcon } from '@ng-icons/core';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { EnergyInterfaceDetailsEditSuccessNotification } from '../../notifications/energy-interface-details-edit-success-notification/energy-interface-details-edit-success-notification';
import { LocationsStore } from '../../../locations/store/locations.store';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../../environments/environment';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-energy-interface-details-address-edit-form',
  templateUrl: './energy-interface-details-address-edit-form.html',
  imports: [Button, NgIcon, InputSimple, FieldErrors, FormField, TranslocoPipe, NgClass],
})
export class EnergyInterfaceDetailsAddressEditForm {
  private store = inject(EnergyInterfacesStore);
  private notificationService = inject(NotificationService);
  protected locationsStore = inject(LocationsStore);
  private translocoService = inject(TranslocoService);

  protected maloMeloMeterArticleUrl = environment.articles.meloMaloMeter;

  energyInterface = input.required<EnergyInterfaceResponse>();
  private energyInterfaceData = computed(() => energyInterfaceToModel(this.energyInterface()));
  organizationId = input.required<string>();
  energyInterfaceId = input.required<string>();

  private state = computed(() =>
    this.store.energyInterfaces()?.find((ei) => ei.id === this.energyInterfaceId()),
  );
  private location = computed(() =>
    this.locationsStore.locations()?.find((loc) => loc.id === this.energyInterface().locationID),
  );

  protected editButtonLoading = signal(false);
  protected enableEdit = signal(false);
  protected isUpdateLoading = computed(() => this.state()?.isEditLoading ?? false);

  protected model = linkedSignal<EnergyInterface>(() =>
    energyInterfaceToModel(this.energyInterface()),
  );
  protected energyInterfaceStatus = computed(() => this.state()?.data?.status ?? 'UNKNOWN');

  protected form = form(this.model, (schemaPath) => {
    required(schemaPath.address.street);
    required(schemaPath.address.streetNumber);
    required(schemaPath.address.zipCode);
    required(schemaPath.address.city);
  });

  protected onClickCancel() {
    this.enableEdit.set(false);
    this.model.set(this.energyInterfaceData());
  }

  protected async onClickEnableEdit() {
    this.editButtonLoading.set(true);
    await new Promise((resolve) => setTimeout(resolve, environment.delays.editEnergyInterfaceMs));
    this.enableEdit.set(true);
    this.editButtonLoading.set(false);
  }

  protected async onClickCopyLocation() {
    const address = this.location()?.data?.address;
    if (!address) {
      this.notificationService.showError({
        title: this.translocoService.translate(
          'cockpit.energyInterfaces.details.address.copyAddressErrorTitle',
        ),
        subtitle: this.translocoService.translate(
          'cockpit.energyInterfaces.details.address.copyAddressErrorSubtitle',
        ),
      });
      return;
    }
    this.model.update((m) => ({
      ...m,
      address: {
        street: address.street,
        streetNumber: address.streetNumber,
        streetNumberSuffix: address.streetNumberSuffix || '',
        city: address.city,
        zipCode: address.zipCode,
      },
    }));
  }

  protected async onSubmit() {
    if (this.form().invalid()) {
      return;
    }
    try {
      await this.store.update(
        this.energyInterfaceId(),
        toEnergyInterfacePayload(this.model()),
        environment.delays.updateEnergyInterfaceMs,
      );
      this.enableEdit.set(false);
      this.notificationService.show({
        component: EnergyInterfaceDetailsEditSuccessNotification,
        duration: 5000,
        componentProps: {
          clickUndoCallback: () => this.onClickUndo(),
        },
      });
    } catch (e) {
      console.error('Failed to update energy interface', e);
      this.notificationService.showError({
        title: this.translocoService.translate(
          'cockpit.energyInterfaces.details.general.updateErrorTitle',
        ),
      });
    }
  }

  private onClickUndo() {
    return this.store.undoUpdate(
      this.energyInterfaceId(),
      environment.delays.updateEnergyInterfaceMs,
    );
  }
}
